import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Page from '../../../Components/Page'
import LoadingSpinner from '../../../Components/LoadingSpinner'
import useRegisterUser from '../../../Hooks/Brief/useRegisterUser'
import useSubmitBrief from '../../../Hooks/Brief/useSubmitBrief'
import useBriefContext from '../../../Hooks/Brief/useBriefContext'
import { useDirectEnquirySpace } from '../../../Hooks/useSpaceById'
import SpaceRecsList, { SpaceRecsNav, useSpaceRecs } from '../../Success/LeadFeed/SpaceRecs'
import { CrossIcon } from '../../Success/LeadFeed/Icons'
import { MIN_REQUESTED_VENUES } from '../../../Constants'
import { requiredGuestAttrs } from '../../../Logic/Form/Sequences/Biz/sequence'
import isDirectEnquiryBrief from '../../../Logic/Form/Sequences/DirectEnquiry/isDirectEnquiryBrief'
import { isLeadFeedCity } from '../../../Logic/Form/Sequences/getRecommendationsTiming'
import InputView from '../../FormSteps/InputView'

// The LF picker, run before submission - this step submits and then creates the favourites.
const LFRecommendationInput: InputView = (): JSX.Element => {
  const { brief } = useBriefContext()
  const navigate = useNavigate()
  const {
    loading,
    error,
    recommendations,
    requestedSpaces,
    shortlistedSpaces,
    hasArrivedWithShortlist,
    onToggleSpace,
    maxRequestedVenues,
    saveRequests,
    needsChoice,
    flagMinimumNotMet,
  } = useSpaceRecs()

  const isDirectEnquiry = isDirectEnquiryBrief(brief)
  // Never gates rendering - the listings service failing must not cost the guest the picker.
  const { space: directSpace, fetching: spaceFetching } = useDirectEnquirySpace(brief)
  const countsPinned = isDirectEnquiry && (directSpace !== null || spaceFetching)

  const [shouldRegisterUser, setShouldRegisterUser] = useState(false)
  const [shouldSubmitBrief, setShouldSubmitBrief] = useState(false)
  const autoSubmitted = useRef(false)

  const { registering, registered } = useRegisterUser({ skip: !shouldRegisterUser })
  const { loading: submitting } = useSubmitBrief({
    skip: !shouldSubmitBrief,
    onSubmitted: (briefId) => saveRequests(briefId),
  })

  useEffect(() => {
    if (!registered || !shouldRegisterUser) return
    setShouldSubmitBrief(true)
  }, [registered])

  const submit = () => {
    if (!brief.guest?.id) {
      const hasRequiredGuestAttrs = requiredGuestAttrs.every((attr) => !!brief.guest?.[attr])
      if (!hasRequiredGuestAttrs) return navigate('/user')
      return setShouldRegisterUser(true)
    }
    setShouldSubmitBrief(true)
  }

  const hasRecommendations = recommendations.length > 0
  const failedToLoad = error !== null && !hasRecommendations
  // Only outside our coverage is an empty result a verdict rather than a blip. Biz briefs and
  // concierge leads never reach this component, and a direct enquiry is served by definition.
  const cannotService =
    !hasRecommendations && !failedToLoad && !isLeadFeedCity(brief) && !isDirectEnquiry

  useEffect(() => {
    if (loading || hasRecommendations || cannotService || autoSubmitted.current) return

    autoSubmitted.current = true
    submit()
  }, [loading, recommendations, error])

  const busy = registering || submitting

  if (busy || loading) {
    return (
      <LoadingSpinner
        fullPage={true}
        accessibleText={
          registering
            ? 'Creating your account...'
            : busy
            ? 'Sending your enquiry...'
            : 'Finding venues for your event...'
        }
      />
    )
  }

  // Submit stays disabled until we decide whether an unservable brief should still create a lead.
  if (cannotService) {
    return (
      <Page
        metaTitle='No venues found - HeadBox'
        heading="We're sorry - we can't service your requirements"
        className='lf-recommendations-page'
        footer={
          <SpaceRecsNav
            requestedCount={0}
            label='Submit'
            onConfirm={submit}
            confirmDisabled={true}
            busy={busy}
          />
        }>
        <div className='recs-unserved'>
          <p className='recs-unserved-icon' aria-hidden='true'>
            <CrossIcon />
          </p>
          <p className='recs-unserved-copy'>
            We couldn&apos;t find any venues that match this brief, so there&apos;s nothing for you
            to choose from here. We&apos;re sorry we couldn&apos;t help this time.
          </p>
        </div>
      </Page>
    )
  }

  if (!hasRecommendations && !isDirectEnquiry) {
    return <LoadingSpinner fullPage={true} accessibleText='Finding venues for your event...' />
  }

  const directVenue = directSpace?.venueName || directSpace?.title
  const heading = isDirectEnquiry
    ? directVenue
      ? `Your enquiry is going to ${directVenue} - add any other venues you would like to hear from`
      : 'Add any other venues you would like to hear from'
    : hasArrivedWithShortlist
    ? "We've kept the venues you shortlisted - add any others you would like to hear from"
    : 'Choose the venues you would like to hear from - no strings attached!'

  return (
    <Page
      metaTitle='Choose your venues - HeadBox'
      heading={heading}
      className='lf-recommendations-page'
      footer={
        <SpaceRecsNav
          requestedCount={requestedSpaces.length}
          // The pinned venue already meets the guest's obligation to choose one.
          minRequested={isDirectEnquiry ? 0 : MIN_REQUESTED_VENUES}
          label='Submit'
          onConfirm={submit}
          busy={busy}
          needsChoice={needsChoice}
          onBelowMinimum={flagMinimumNotMet}
          shortlistedSpaces={shortlistedSpaces}
          onRemoveVenue={(space) => onToggleSpace(Number(space.id), space.hostID)}
          pinned={directSpace}
          defaultOpen={isDirectEnquiry || hasArrivedWithShortlist}
          countSuffix={countsPinned ? 'on your enquiry' : 'shortlisted'}
        />
      }>
      <SpaceRecsList
        recommendations={recommendations}
        loading={loading}
        requestedSpaces={requestedSpaces}
        onToggleSpace={onToggleSpace}
        maxRequestedVenues={maxRequestedVenues}
        needsChoice={needsChoice}
        emptyMessage={
          isDirectEnquiry
            ? 'We could not find any other venues to suggest alongside this one.'
            : undefined
        }
      />
    </Page>
  )
}

export default LFRecommendationInput
